"use client";

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import { useQuery } from "@tanstack/react-query";
import { Menu, X, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { propertiesApi } from "@/lib/api/properties";

export function MobileNav() {
  const t = useTranslations("Navigation");
  const pathname = usePathname();

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const [isOpen, setIsOpen] = useState(false);

  // Accordion states
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const [goalsOpen, setGoalsOpen] = useState(false);
  const [propertiesOpen, setPropertiesOpen] = useState(false);

  // Close main drawer and reset accordion states
  const closeMenu = () => {
    setIsOpen(false);
  };

  // Reset accordions when opening/closing main menu
  useEffect(() => {
    if (!isOpen) {
      setAccountsOpen(false);
      setPortfolioOpen(false);
      setGoalsOpen(false);
      setPropertiesOpen(false);
    }
  }, [isOpen]);

  // Fetch properties for the properties submenu
  const { data: properties } = useQuery({
    queryKey: ["properties"],
    queryFn: propertiesApi.listProperties,
    staleTime: 60_000,
  });

  const hasProperties = (properties?.length ?? 0) > 0;

  // Active status helper
  const isOverviewActive = pathname === "/";
  const isAccountsListActive = pathname === "/accounts" || (pathname.startsWith("/accounts/") && !pathname.startsWith("/accounts/integrations"));
  const isIntegrationsActive = pathname.startsWith("/accounts/integrations");
  const isAccountsActive = isAccountsListActive || isIntegrationsActive;
  const isAnalysisActive = pathname === "/portfolio/analysis" || pathname.startsWith("/portfolio/analysis/");
  const isPortfolioActive = (pathname === "/portfolio" || pathname.startsWith("/portfolio/")) && !isAnalysisActive;
  const isActivitiesActive = pathname === "/activities" || pathname.startsWith("/activities/");
  const isMyPortfolioSectionActive = isPortfolioActive || isActivitiesActive || isAnalysisActive;
  const isGoalsListActive = pathname === "/goals";
  const isGoalsCalculatorActive = pathname === "/goals/calculadora";
  const isGoalsActive = isGoalsListActive || isGoalsCalculatorActive;
  const isPropertiesActive = pathname === "/properties" || pathname.startsWith("/properties/");

  // Close when pathname changes
  useEffect(() => {
    closeMenu();
  }, [pathname]);

  // Prevent background scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <div className="md:hidden">
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="flex h-10 w-10 items-center justify-center rounded-md border border-border bg-surface text-text hover:bg-surface-2 transition-colors cursor-pointer"
        aria-label="Open Menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {mounted && createPortal(
        <>
          {/* Backdrop */}
          {isOpen && (
            <div
              onClick={closeMenu}
              className="fixed inset-0 z-40 bg-ground/80 backdrop-blur-sm transition-opacity duration-300"
            />
          )}

          {/* Drawer Panel */}
          <div
            className={cn(
              "fixed inset-y-0 right-0 z-50 w-full max-w-xs bg-surface border-l border-border p-6 shadow-xl flex flex-col transition-transform duration-300 ease-in-out transform",
              isOpen ? "translate-x-0" : "translate-x-full"
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border pb-4 mb-4">
              <Link href="/" onClick={closeMenu} className="text-base font-bold text-text tracking-tight">
                The Profit Prophet
              </Link>
              <button
                onClick={closeMenu}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-surface text-text hover:bg-surface-2 transition-colors cursor-pointer"
                aria-label="Close Menu"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Links Navigation */}
            <nav className="flex-1 overflow-y-auto space-y-2 pr-2">
              {/* Overview */}
              <Link
                href="/"
                onClick={closeMenu}
                className={cn(
                  "flex w-full items-center px-4 py-2.5 text-sm font-medium rounded-md transition-colors",
                  isOverviewActive ? "bg-surface-2 text-text" : "text-muted hover:text-text"
                )}
              >
                {t("overview")}
              </Link>

              {/* Accounts Collapsible */}
              <div className="space-y-1">
                <button
                  onClick={() => setAccountsOpen(!accountsOpen)}
                  className={cn(
                    "flex w-full items-center justify-between px-4 py-2.5 text-sm font-medium rounded-md transition-colors cursor-pointer",
                    isAccountsActive ? "bg-surface-2 text-text" : "text-muted hover:text-text"
                  )}
                >
                  <span>{t("accounts")}</span>
                  <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", accountsOpen && "rotate-180")} />
                </button>
                {accountsOpen && (
                  <div className="pl-4 space-y-1">
                    <Link
                      href="/accounts"
                      onClick={closeMenu}
                      className={cn(
                        "flex w-full items-center px-4 py-2 text-sm rounded-md transition-colors",
                        isAccountsListActive ? "text-text font-medium" : "text-muted hover:text-text"
                      )}
                    >
                      {t("accountsList")}
                    </Link>
                    <Link
                      href="/accounts/integrations"
                      onClick={closeMenu}
                      className={cn(
                        "flex w-full items-center px-4 py-2 text-sm rounded-md transition-colors",
                        isIntegrationsActive ? "text-text font-medium" : "text-muted hover:text-text"
                      )}
                    >
                      {t("integrations")}
                    </Link>
                  </div>
                )}
              </div>

              {/* My Portfolio Collapsible */}
              <div className="space-y-1">
                <button
                  onClick={() => setPortfolioOpen(!portfolioOpen)}
                  className={cn(
                    "flex w-full items-center justify-between px-4 py-2.5 text-sm font-medium rounded-md transition-colors cursor-pointer",
                    isMyPortfolioSectionActive ? "bg-surface-2 text-text" : "text-muted hover:text-text"
                  )}
                >
                  <span>{t("myPortfolio")}</span>
                  <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", portfolioOpen && "rotate-180")} />
                </button>
                {portfolioOpen && (
                  <div className="pl-4 space-y-1">
                    <Link
                      href="/portfolio"
                      onClick={closeMenu}
                      className={cn(
                        "flex w-full items-center px-4 py-2 text-sm rounded-md transition-colors",
                        isPortfolioActive ? "text-text font-medium" : "text-muted hover:text-text"
                      )}
                    >
                      {t("portfolio")}
                    </Link>
                    <Link
                      href="/portfolio/analysis"
                      onClick={closeMenu}
                      className={cn(
                        "flex w-full items-center px-4 py-2 text-sm rounded-md transition-colors",
                        isAnalysisActive ? "text-text font-medium" : "text-muted hover:text-text"
                      )}
                    >
                      {t("analysis")}
                    </Link>
                    <Link
                      href="/activities"
                      onClick={closeMenu}
                      className={cn(
                        "flex w-full items-center px-4 py-2 text-sm rounded-md transition-colors",
                        isActivitiesActive ? "text-text font-medium" : "text-muted hover:text-text"
                      )}
                    >
                      {t("activities")}
                    </Link>
                  </div>
                )}
              </div>

              {/* Goals Collapsible */}
              <div className="space-y-1">
                <button
                  onClick={() => setGoalsOpen(!goalsOpen)}
                  className={cn(
                    "flex w-full items-center justify-between px-4 py-2.5 text-sm font-medium rounded-md transition-colors cursor-pointer",
                    isGoalsActive ? "bg-surface-2 text-text" : "text-muted hover:text-text"
                  )}
                >
                  <span>{t("goals")}</span>
                  <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", goalsOpen && "rotate-180")} />
                </button>
                {goalsOpen && (
                  <div className="pl-4 space-y-1">
                    <Link
                      href="/goals"
                      onClick={closeMenu}
                      className={cn(
                        "flex w-full items-center px-4 py-2 text-sm rounded-md transition-colors",
                        isGoalsListActive ? "text-text font-medium" : "text-muted hover:text-text"
                  )}
                >
                  {t("goalsList")}
                </Link>
                <Link
                  href="/goals/calculadora"
                  onClick={closeMenu}
                  className={cn(
                    "flex w-full items-center px-4 py-2 text-sm rounded-md transition-colors",
                    isGoalsCalculatorActive ? "text-text font-medium" : "text-muted hover:text-text"
                  )}
                >
                  {t("goalsCalculator")}
                </Link>
              </div>
            )}
          </div>

          {/* Properties Collapsible / Direct Link */}
          <div className="space-y-1">
            <button
              onClick={() => {
                if (hasProperties) {
                  setPropertiesOpen(!propertiesOpen);
                } else {
                  closeMenu();
                  window.location.href = "/properties";
                }
              }}
              className={cn(
                "flex w-full items-center justify-between px-4 py-2.5 text-sm font-medium rounded-md transition-colors cursor-pointer",
                isPropertiesActive ? "bg-surface-2 text-text" : "text-muted hover:text-text"
              )}
            >
              <span>{t("properties")}</span>
              {hasProperties && (
                <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", propertiesOpen && "rotate-180")} />
              )}
            </button>
            {propertiesOpen && hasProperties && (
              <div className="pl-4 space-y-1">
                <Link
                  href="/properties"
                  onClick={closeMenu}
                  className={cn(
                    "flex w-full items-center px-4 py-2 text-sm rounded-md transition-colors",
                    pathname === "/properties" ? "text-text font-medium" : "text-muted hover:text-text"
                  )}
                >
                  {t("overview") || "Overview"}
                </Link>
                {properties!.map((property) => {
                  const active = pathname === `/properties/${property.id}`;
                  return (
                    <Link
                      key={property.id}
                      href={`/properties/${property.id}`}
                      onClick={closeMenu}
                      className={cn(
                        "flex w-full items-center px-4 py-2 text-sm rounded-md transition-colors truncate",
                        active ? "text-text font-medium" : "text-muted hover:text-text"
                      )}
                    >
                      {property.name}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </nav>
      </div>
    </>,
    document.body
  )}
</div>
  );
}
